import last from 'lodash-es/last';
import { noop } from 'rxjs';
import { isPhrasingContent, isPresentationalPhrasingContent } from './dom/content';
import { unwrapElement } from './dom/element';
import { findAncestor, findNextChildOrSibling, findNodeInRange, isElement } from './dom/node';
import { paintNodesInRange } from './dom/paint';
import {
  createPointAtEndOfNode,
  createPointAtStartOfNode,
  createPointWithEndOfRange,
  createPointWithStartOfRange,
  createRangeWithPoints,
} from './dom/point';
import { nodeHasTextColor, removeTextColorFromElement } from './style/textColor';

export function nodeHasLink(node: Node, container: Node): boolean {
  return !!findLinkAncestor(node, container);
}

export function findLinkAncestor(node: Node, container: Node): HTMLAnchorElement | null {
  return findAncestor(node, nodeIsLink, container);
}

export function nodeIsLink(node: Node): node is HTMLAnchorElement {
  return isElement(node) && node.tagName === 'A';
}

export function findLinkInRange(container: Node, range: Range): HTMLAnchorElement | null {
  return findNodeInRange(container, range, node => findLinkAncestor(node, container));
}

export function removeLinksInRange(container: Node, range: Range): Range {
  const document = container.ownerDocument;
  if (!document) {
    return range;
  }

  let returnStartPoint = createPointWithStartOfRange(range);
  let returnEndPoint = createPointWithEndOfRange(range);

  const startPoint = returnStartPoint.moveToStart();
  const endPoint = returnEndPoint.moveToEnd();

  let node = startPoint.findNextNode(container);
  while (node) {
    if (endPoint.intersectsNode(node) && !endPoint.isAtEndOfNode(node)) {
      node = node.firstChild;
      continue;
    }

    if (!endPoint.isAfterNode(node)) {
      node = null;
      break;
    }

    const nextNode = findNextChildOrSibling(node, container);

    const linkNode = findLinkAncestor(node, container);
    if (linkNode) {
      if (endPoint.isAtEndOfNode(linkNode)) {
        returnEndPoint = createPointAtEndOfNode(nextNode || container);
      }

      if (startPoint.isAtStartOfNode(linkNode)) {
        returnStartPoint = nextNode ? createPointAtStartOfNode(nextNode) : endPoint;
      }

      unwrapElement(linkNode);
    }

    node = nextNode;
  }

  return createRangeWithPoints(document, returnStartPoint, returnEndPoint);
}

export function addLinkToRange(
  container: Node,
  range: Range,
  decorateLink: (element: HTMLAnchorElement) => void = noop,
): Range {
  const { commonAncestorContainer: commonAncestor } = range;
  const { ownerDocument } = commonAncestor;

  // Add the link(s).
  const newLinks: HTMLAnchorElement[] = [];
  paintNodesInRange(container, range, {
    shouldPaintNode: node => isPhrasingContent(node) && !nodeHasLink(node, container),
    // Split phrasing content to favor one continuous link, but don't split unnecessarily beyond the common
    // ancestor.
    shouldSplitElement: node => isPresentationalPhrasingContent(node) && !node.contains(commonAncestor),
    createPaintedElement: document => {
      const link = document.createElement('a');
      decorateLink(link);

      newLinks.push(link);
      return link;
    },
  });

  // Remove nested text colors, because they make it hard to see links.
  // TODO: Should we also strip underlines?
  newLinks.forEach(link => {
    const linkRange = range.cloneRange();
    linkRange.selectNodeContents(link);

    paintNodesInRange(link, linkRange, {
      shouldPaintNode: node => nodeHasTextColor(node, link),
      paintElement: element => removeTextColorFromElement(element),
    });
  });

  // Restore the range.

  const lastLink = last(newLinks);

  if (!ownerDocument || !lastLink) {
    return range;
  }

  const returnRange = ownerDocument.createRange();
  returnRange.setStartAfter(lastLink);
  returnRange.setEndAfter(lastLink);

  return returnRange;
}
